import { toast } from '#src/utils/toast.ts'

/* 1440×740 — the resolution we build for: roughly people's default screen
 * resolution with a visible dock, which eats into the vertical space. Defined
 * once here so every "open in new window" command stays the same size. */
const WINDOW_SIZE_FEATURES = 'width=1440,height=740'

/* Opens `url` in a separate browser window rather than a tab.
 *
 * The width/height features are what make the browser spawn a separate
 * window instead of a tab — but they also force a *chromeless* popup (no
 * tabs/address bar). There is no web API to open a full-chrome browser
 * window: the legacy toolbar/location/menubar features are ignored by
 * modern browsers (an anti-phishing measure), and passing no features
 * just yields a tab. So a chromeless window is the only way to guarantee
 * a separate window.
 *
 * Must be called synchronously inside a user gesture or the browser will
 * block it. We keep the `window.opener` handle (no `noopener`) so we can
 * always warn the user when the open silently degrades — a blocked pop-up,
 * or a browser that ignored the window features and opened a tab (notably
 * Arc). */
export function openInNewWindow(url: string): Window | null {
  const popup = window.open(url, '_blank', `popup,${WINDOW_SIZE_FEATURES}`)
  warnIfWindowOpenDegraded(popup, url)
  return popup
}

/* Tells the user when "open in new window" silently didn't do what they asked.
 *
 * `window.open` hands back the same kind of handle (or null) whether the
 * browser honored the window features, ignored them and opened a tab, or
 * blocked the pop-up — the spec exposes no flag for which happened. We infer:
 *
 * - null → the pop-up was blocked (nothing opened).
 * - A tab is just a new tab in *this* browser window, so it reports the
 *   opener's exact outer size and screen position. A real separate window
 *   gets its own size (≈1440×740) and an offset position. Requiring all four
 *   to match is a strong "this became a tab" signal, and the position check
 *   avoids false alarms when a real window happens to match the opener's size.
 *
 * Read after a short delay so the OS has placed and sized the window. Reading
 * `popup.outerWidth`/`screenX` needs same-origin access; the popup can also
 * navigate cross-origin within the delay, so the reads are guarded and we stay
 * silent on failure. Best-effort and browser-dependent: it stays silent when
 * unsure rather than risk a wrong "opened as a tab" toast. */
function warnIfWindowOpenDegraded(popup: Window | null, url: string): void {
  if (popup === null) {
    /* Only warn about a blocked pop-up when we actually pointed it at a URL.
     * The create-note flow pre-opens an empty popup (`openInNewWindow('')`) and
     * has its own same-tab fallback, so a "blocked, try again" toast there would
     * contradict the note that just opened in the current tab. */
    if (url !== '') {
      toast.error(
        'Could not open a new window — pop-ups are blocked. Allow pop-ups for this site and try again.'
      )
    }
    return
  }
  window.setTimeout(() => {
    if (popup.closed) {
      return
    }
    try {
      if (
        popup.outerWidth === window.outerWidth &&
        popup.outerHeight === window.outerHeight &&
        popup.screenX === window.screenX &&
        popup.screenY === window.screenY
      ) {
        toast(
          'Opened in a new tab — your browser opened this as a tab instead of a separate window.'
        )
      }
    } catch {
      // The popup can navigate cross-origin within the delay (e.g. a session
      // redirect), making these reads throw; stay silent rather than throw.
    }
  }, 300)
}
